import { useState } from "react";
import { Navigate, Outlet, useNavigate, useOutletContext } from "react-router";

import {
  Badge,
  BadgeColor,
  ConfirmModal,
  DescriptionList,
  Modal,
  PageHeading,
  Spinner,
} from "@prequel/shared-components";

import ProductForm from "../ProductForm";

import { useAllModels } from "@/api/queries/models";
import { useDeleteProduct, useProduct } from "@/api/queries/products";
import Breadcrumbs from "@/components/Breadcrumbs";
import DangerZoneSection from "@/components/DangerZone";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import BufoPage from "@/pages/BufoPage";
import { isBufoUUID } from "@/pages/BufoPage/bufo";
import { ExistingProduct } from "@/types";

type ProductContext = {
  product: ExistingProduct;
};

// Parent wrapper: Loads data once, provides via context
const ProductPage = () => {
  const productID = useRequiredParam("productID");
  const { data: product, isLoading: isLoadingProduct } = useProduct(productID);

  // Check for easter egg before rendering
  if (isBufoUUID(productID)) {
    return <BufoPage />;
  }

  if (isLoadingProduct) {
    return <Spinner />;
  }

  if (!product) {
    return <Navigate to="/models/products" replace />;
  }

  return <Outlet context={{ product }} />;
};

// Details page: Full page component, gets data from context
export const ProductDetailsPage = () => {
  const navigate = useNavigate();
  const { product } = useOutletContext<ProductContext>();
  const productID = product.id;
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: models } = useAllModels();
  const deleteProductMutation = useDeleteProduct();

  const onDelete = () => setShowDeleteModal(true);

  const onConfirmDelete = () => {
    setShowDeleteModal(false);
    if (product) {
      deleteProductMutation.mutate({
        id: product.id,
        redirectPath: "/models/products",
      });
    }
  };

  const dangerZoneActions = [
    {
      label: "Edit product",
      description: "Edit product fields.",
      buttonText: "Edit product",
      onClick: () => navigate(`/models/products/${productID}/edit`),
    },
    {
      label: "Delete product",
      description: "Once you delete a product, the action cannot be undone.",
      buttonText: "Delete product",
      onClick: onDelete,
    },
  ];

  return (
    <>
      <div className="max-w-2xl px-7 py-4">
        <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
          <ConfirmModal
            title="Delete product"
            message="Are you sure you want to delete this product? This action cannot be undone."
            onConfirm={onConfirmDelete}
            confirmButtonText="Delete"
            onCancel={() => setShowDeleteModal(false)}
          />
        </Modal>
        <PageHeading
          title={product.product_name}
          onGoBack={() => navigate("/models/products")}
          breadcrumbs={<Breadcrumbs dynamicLabel={product.product_name} />}
        />
        <div className="mt-10 divide-y divide-gray-200">
          <div className="space-y-1 pb-6">
            <h3 className="text-lg/6 font-medium text-gray-900">Details</h3>
          </div>
          <div>
            <DescriptionList>
              <DescriptionList.Item label="ID" value={product.id} />
              <DescriptionList.Item label="Description" value={product.description} />
              <DescriptionList.Item
                label="Models"
                value={(() => {
                  return product.models.map((model) => {
                    const selectedModel = models?.find((m) => m.model_name === model);
                    return (
                      <Badge
                        key={model}
                        className="mr-2 mb-2"
                        text={selectedModel ? selectedModel.model_name : model}
                        color={BadgeColor.GRAY}
                        onClick={() => navigate(`/models/${selectedModel?.id}`)}
                      />
                    );
                  });
                })()}
              />
            </DescriptionList>
          </div>
        </div>
        {!product.is_system_product && <DangerZoneSection actions={dangerZoneActions} />}
      </div>
    </>
  );
};

// Edit page: Full page component, gets data from context
export const ProductEditPage = () => {
  const navigate = useNavigate();
  const { product } = useOutletContext<ProductContext>();
  const productID = product.id;

  return (
    <div className="max-w-xl px-7 py-4">
      <PageHeading
        title="Edit Product"
        onGoBack={() => navigate(`/models/products/${productID}`)}
        breadcrumbs={<Breadcrumbs dynamicLabel={product.product_name} />}
      />
      <ProductForm />
    </div>
  );
};

export default ProductPage;
